import React, { useEffect } from 'react'
import { Redirect, useLocation, useRouteMatch } from 'react-router-dom'

import { post_event } from 'utils/analytics'

import { getCookie } from 'utils/cookies'
import Footer from 'components/footer'
import Content from 'components/modular/content'
import { PageLoader } from 'components/loader'

import {
  LOGIN_PATH,
  LOGOUT_PATH,
  STORY_TREE_PATH,
  BOOKFAIR_PATH,
  PENDING_PAYMENT_PATH,
  FOREST_PATH,
  FOREST_TREES_PATH,
  LEAGUE_PATH,
  MY_READINGS_MODULAR_READING_PATH,
  MY_READINGS_MODULAR_READ_PATH,
  MY_READINGS_MODULAR_INDICATED_PATH,
  MY_READINGS_MODULAR_FAVORITES_PATH,
  EXPLORE_PATH,
  SEARCH_PATH_ROOT,
  MY_ACCESS_PATH,
  MY_NOTIFICATION_PREFERENCES_PATH,
  MY_ACCOUNT_PATH,
  ROOT_PATH,
  LIVROS_HOME_ROOT_PATH,
  LER_HOME_ROOT_PATH,
  LER_REVISTAS_E_JORNAIS_HOME_ROOT_PATH,
  LER_NOTICIAS_HOME_ROOT_PATH,
  ATUALIDADES_HOME_ROOT_PATH,
  INGLES_HOME_ROOT_PATH,
  COMUNICAR_HOME_ROOT_PATH,
  SENTIR_HOME_ROOT_PATH,
  SENTIR_DIARIO_HOME_ROOT_PATH,
  READER_SELECT_CLASS_PATH
} from 'routes/paths'
import StoryTreeNav from 'components/story-tree-nav/StoryTreeNav'
import StoryTreeHeader from 'components/story-tree-header/StoryTreeHeader'
import { ServerError } from 'screens/http-status'
import { setHotJarUserData } from 'utils/ui-trackers/hotjar'
import ApiError from 'api/error'
import redirects, { type RedirectsPrevLocationState } from 'routes/redirects'
import { FAMILY_HOME_URL, isFamilyReader } from 'routes/redirects/family'
import type { LanguageTokens } from 'i18n'
import { i18n, useTranslation } from 'i18n'
import usePageTitle from 'utils/hooks/use-page-title'
import HeaderModular from 'components/header-modular'
import { useLanguageCheck } from 'utils/hooks/use-language-check'
import { useNotInArvoreToast } from 'routes/hooks/use-not-in-arvore-toast'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import useSetUserToThirdParty from 'utils/hooks/use-set-user-to-third-party'
import OttoAiSpotlight from 'components/header-modular/header-desktop/OttoAiSpotlight'
import { ActivitiesFirstAssessmentSpotlight } from 'components/modular/home/activities-first-assessment-spotlight'
import HomeStoryTree from 'screens/site/home/story-tree/HomeStoryTree'
import InactiveContract from 'screens/site/inactive-contract'
import {
  onboardingControllerFindOrCreateTutorialEndpoint,
  TutorialResponseDtoState
} from 'api.gen'
import { useGetTeacherClassesQuery } from '__gql-generated__/types'
import {
  shouldRedirectToBookfair,
  shouldRedirectToPayment
} from 'utils/family-paying/paymentStatusHelpers'
import useFeatureToggles from 'utils/hooks/use-feature-toggles'
import { useSuperAutorEligible } from 'utils/hooks/use-super-autor-eligible/useSuperAutorEligible'
import { useSelectClassEligibility } from 'utils/hooks/use-select-class-eligibility'
import { useSaveDeviceProfile } from 'components/header-modular/select-profile-item/useSaveDeviceProfile'
import { useWritingHomeRedirect } from 'utils/hooks/use-writing-home-redirect'
import { useSchoolWelcomeRedirect } from 'utils/hooks/use-school-welcome-redirect'
import { READER_WITH_MULTIPLE_CLASSES_HAS_CHOSEN_CLASS } from 'config/vars'

async function createOnboardingV3Tutorial(): Promise<TutorialResponseDtoState | null> {
  try {
    const response = await onboardingControllerFindOrCreateTutorialEndpoint({
      name: 'onboarding_aluno_v3'
    })

    if (response.data) {
      return response.data.state
    }
    return null
  } catch {
    return null
  }
}

type PrivateRouteProps = {
  readingLayout?: boolean
  forestLayout?: boolean
  noPaddingForMobileScreen?: boolean
  noPaddingForAnyScreen?: boolean
  noWidth?: boolean
  title?: LanguageTokens
  subtitle?: LanguageTokens | React.ComponentType
  dontShowDesktopTitle?: boolean
  pathGoBack?: string
  contentClassName?: string
  component: React.ComponentType
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  readingLayout,
  forestLayout,
  noPaddingForMobileScreen,
  noPaddingForAnyScreen,
  noWidth,
  title,
  subtitle,
  dontShowDesktopTitle,
  pathGoBack,
  component: Component,
  contentClassName
}) => {
  const { path } = useRouteMatch()
  const location = useLocation<RedirectsPrevLocationState>()
  const { t } = useTranslation()

  usePageTitle(title ? t(title) : undefined)
  const {
    user,
    loading,
    error,
    refetch: refetchUser
  } = useUserInfo({
    errorPolicy: 'all'
  })
  const { isLoadingFeatureTogglesData } = useFeatureToggles()
  const isFamilyAdult = isFamilyReader(user)
  const { hasCriarModuleAndFlag } = useSuperAutorEligible()
  const { shouldRenderSelectClass } = useSelectClassEligibility()
  useSaveDeviceProfile()

  const { data: teacherClassesData, loading: loadingTeacherClasses } =
    useGetTeacherClassesQuery({
      skip: !user?.teacher
    })

  const writingHomeRedirect = useWritingHomeRedirect(
    Boolean(user?.id) && !user?.reader
  )

  const schoolWelcomeRedirect = useSchoolWelcomeRedirect(
    Boolean(user?.id) && Boolean(user?.teacher)
  )

  const hasTeacherClasses =
    teacherClassesData?.getTeacherClasses &&
    teacherClassesData.getTeacherClasses.length > 0

  useEffect(() => {
    if (isFamilyAdult) window.location.replace(FAMILY_HOME_URL)
  }, [isFamilyAdult])

  useEffect(() => {
    if (user?.isContextNonEducational) i18n.changeLanguage('nonSchool')
  }, [user])

  useEffect(() => {
    if (
      !isFamilyAdult &&
      shouldRenderSelectClass &&
      location.pathname !== READER_SELECT_CLASS_PATH &&
      !sessionStorage.getItem(READER_WITH_MULTIPLE_CLASSES_HAS_CHOSEN_CLASS)
    ) {
      window.location.href = READER_SELECT_CLASS_PATH
    }
  }, [isFamilyAdult, shouldRenderSelectClass, location.pathname])

  const canAccessStoryTree = Boolean(
    user?.featureToggles?.storyTree && location.pathname === STORY_TREE_PATH
  )

  const STORY_TREE_PATHS = [
    FOREST_PATH,
    FOREST_TREES_PATH,
    LEAGUE_PATH,
    MY_READINGS_MODULAR_INDICATED_PATH,
    MY_READINGS_MODULAR_READING_PATH,
    MY_READINGS_MODULAR_READ_PATH,
    MY_READINGS_MODULAR_FAVORITES_PATH,
    EXPLORE_PATH,
    MY_ACCESS_PATH,
    MY_NOTIFICATION_PREFERENCES_PATH,
    MY_ACCOUNT_PATH
  ]

  const isStoryTreeSearchPath =
    location.pathname === SEARCH_PATH_ROOT ||
    location.pathname.startsWith(`${SEARCH_PATH_ROOT}/`)

  const isStoryTreeBookDetailPath = location.pathname.startsWith(
    `${ROOT_PATH}/livro/`
  )

  const isStoryTreePath = Boolean(
    user?.featureToggles?.storyTree &&
      (STORY_TREE_PATHS.includes(location.pathname) ||
        isStoryTreeSearchPath ||
        isStoryTreeBookDetailPath)
  )

  const useStoryTreeNav = isStoryTreePath
  const useStoryTreeHeader = isStoryTreePath

  useSetUserToThirdParty(user)

  useLanguageCheck()

  useNotInArvoreToast(user)

  useEffect(() => {
    post_event('visit', location.pathname)
  }, [location.pathname])

  useEffect(() => {
    if (!user.id || loading || isFamilyAdult) return

    const isBookfairPath = location.pathname.startsWith(BOOKFAIR_PATH)
    const isBookfairSuccessPath =
      location.pathname === `${BOOKFAIR_PATH}/success`
    const isPendingPaymentPath =
      location.pathname.startsWith(PENDING_PAYMENT_PATH)
    const canAccessOnboarding = user.canAccessOnboarding
    const hasOnboardingV3Enabled = user?.featureToggles?.onboardingAlunoV3
    const needsToPayBookfair = shouldRedirectToBookfair(user)
    const needsToPayPayment = shouldRedirectToPayment(user)

    if (isBookfairSuccessPath && refetchUser) {
      refetchUser()
    }

    if (
      canAccessOnboarding &&
      hasOnboardingV3Enabled &&
      !user?.featureToggles?.storyTree &&
      !needsToPayBookfair &&
      !isBookfairPath &&
      !needsToPayPayment &&
      !isPendingPaymentPath
    ) {
      let cancelled = false

      createOnboardingV3Tutorial()
        .then(state => {
          if (!cancelled && state === TutorialResponseDtoState.pending) {
            window.location.href =
              'https://livros.arvore.com.br/app-v2/onboarding/introduction'
          }
        })
        .catch()

      return () => {
        cancelled = true
      }
    }
  }, [user, loading, isFamilyAdult, location.pathname, refetchUser])

  if (isFamilyAdult) {
    return <PageLoader />
  }

  if (Boolean(getCookie('access_token')) === false) {
    window.location.href = LOGIN_PATH
    return null
  }

  if (ApiError.hasServerError(error) && getCookie('access_token')) {
    // Token is now managed via cookies
    return <ServerError />
  }

  if ((loading && !user.id) || isLoadingFeatureTogglesData) {
    return <PageLoader />
  }

  if (user.id) {
    const { reader } = user

    if (ApiError.errorMessageContains(error, 'inactive_contract')) {
      return <InactiveContract hasCriarModuleAndFlag={hasCriarModuleAndFlag} />
    }

    if (!reader) {
      if (writingHomeRedirect.loading) {
        return <PageLoader />
      }

      if (writingHomeRedirect.redirectTo) {
        window.location.href = writingHomeRedirect.redirectTo
        return null
      }
    }

    if (schoolWelcomeRedirect.loading) {
      return <PageLoader />
    }

    if (schoolWelcomeRedirect.redirectTo) {
      window.location.href = schoolWelcomeRedirect.redirectTo
      return null
    }

    // check if user has been disabled
    if (!reader || ApiError.errorMessageContains(error, 'desativated')) {
      // Token is now managed via cookies

      let errorMessage =
        'Sua conta está desativada. Por favor entre em contato com a sua instituição.'

      if (ApiError.errorMessageContains(error, 'last_day_expired')) {
        errorMessage =
          'Sua conta foi desativada por exceder o período de acesso. Por favor entre em contato com a sua instituição.'
      }

      global.alert(errorMessage)
      return <Redirect to={LOGOUT_PATH} />
    }

    if (
      document.body.className.indexOf('admin') >= 0 ||
      document
        .querySelector('meta[name="viewport"]')
        .getAttribute('content') === ''
    ) {
      document.body.className = document.body.className.replace('admin', '')
      document
        .querySelector('meta[name="viewport"]')
        .setAttribute('content', 'width=device-width, initial-scale=1')
    }

    setHotJarUserData(user)

    if (
      user?.teacher &&
      user?.featureToggles?.onboardingTeacher &&
      !user?.featureToggles?.storyTree &&
      user.name &&
      !loadingTeacherClasses
    ) {
      const needsOnboarding =
        !user.filledOnboardingJobInfo && !hasTeacherClasses

      if (needsOnboarding) {
        window.location.href =
          'https://livros.arvore.com.br/app-v2/onboarding/teacher'
        return null
      }
    }

    const locationToRedirect = redirects.getLocationToRedirect({
      user: user,
      routePath: path,
      prevState: location.state,
      isSuperAutorEligible: hasCriarModuleAndFlag
    })

    if (locationToRedirect.pathname) {
      return <Redirect to={locationToRedirect} />
    }

    const storyTreeHomePaths = [
      ROOT_PATH,
      LIVROS_HOME_ROOT_PATH,
      LER_HOME_ROOT_PATH,
      LER_REVISTAS_E_JORNAIS_HOME_ROOT_PATH,
      LER_NOTICIAS_HOME_ROOT_PATH,
      ATUALIDADES_HOME_ROOT_PATH,
      INGLES_HOME_ROOT_PATH,
      COMUNICAR_HOME_ROOT_PATH,
      SENTIR_HOME_ROOT_PATH,
      SENTIR_DIARIO_HOME_ROOT_PATH
    ]

    if (
      user?.featureToggles?.storyTree &&
      storyTreeHomePaths.includes(location.pathname)
    ) {
      window.location.href = user?.teacher
        ? '/app-v2/admin/class-journey-report'
        : '/app-v2/story-tree'
      return null
    }

    if (canAccessStoryTree) {
      return (
        <ActivitiesFirstAssessmentSpotlight>
          <OttoAiSpotlight>
            <HeaderModular title={title} />
            <HomeStoryTree />
          </OttoAiSpotlight>
        </ActivitiesFirstAssessmentSpotlight>
      )
    }

    if (forestLayout) {
      return (
        <ActivitiesFirstAssessmentSpotlight>
          <OttoAiSpotlight>
            {useStoryTreeHeader ? (
              <StoryTreeHeader />
            ) : (
              <HeaderModular title={title} />
            )}
            <Component />
            {useStoryTreeNav ? <StoryTreeNav /> : <Footer />}
          </OttoAiSpotlight>
        </ActivitiesFirstAssessmentSpotlight>
      )
    }

    if (readingLayout) return <Component />

    return (
      <ActivitiesFirstAssessmentSpotlight>
        <OttoAiSpotlight>
          {useStoryTreeHeader ? (
            <StoryTreeHeader />
          ) : (
            <HeaderModular title={title} pathGoBack={pathGoBack} />
          )}
          <Content
            className={contentClassName}
            title={title}
            subtitle={subtitle}
            pathGoBack={pathGoBack}
            noPaddingForMobileScreen={noPaddingForMobileScreen}
            noPaddingForAnyScreen={noPaddingForAnyScreen}
            noWidth={noWidth}
            dontShowDesktopTitle={dontShowDesktopTitle}
          >
            <Component />
          </Content>
          {useStoryTreeNav ? <StoryTreeNav /> : <Footer />}
        </OttoAiSpotlight>
      </ActivitiesFirstAssessmentSpotlight>
    )
  }

  // prevent a infinity loop in case if we have a stored token in
  // localStorage but that token is invalid
  // Token is now managed via cookies

  return (
    !loading && (
      <Redirect
        to={{
          pathname: LOGIN_PATH,
          search: `redirectTo=${encodeURIComponent(location.pathname)}`
        }}
      />
    )
  )
}

export default PrivateRoute
